import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Pagination,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import CustomSelect from "../../../../components/CustomSelect";
import ScreenLayout from "../../../../components/ScreenLayout";
import PageHeader from "../../../../components/PageHeader";
import axiosClient from "../../../../utils/api/axiosClient";
import { getToken } from "../../../../utils/functions/index.js";

interface ContactRow {
  id: number;
  userId: number;
  deviceId?: number | null;
  name?: string | null;
  mobileNumber?: string | null;
  created_at?: string | null;
}

interface ContactDeviceRow {
  userId: number;
  username?: string | null;
  deviceId: number;
  version?: string | null;
  label: string;
  nativeState?: string | null;
  currentState?: string | null;
  isDownloaded?: boolean | null;
}

interface CountryOption {
  id: number;
  name: string;
}

interface StateOption {
  id: number;
  name: string;
  countryId: number;
}

interface CityOption {
  id: number;
  name: string;
  stateId: number;
  countryId: number;
}

interface ContactListFilters {
  nativeCountry: number | null;
  nativeState: number | null;
  nativeCity: number | null;
  currentCountry: number | null;
  currentState: number | null;
  currentCity: number | null;
}

type DownloadedSort = "asc" | "desc" | null;

const DEFAULT_PAGE_SIZE = 25;
const EMPTY_FILTERS: ContactListFilters = {
  nativeCountry: null,
  nativeState: null,
  nativeCity: null,
  currentCountry: null,
  currentState: null,
  currentCity: null,
};

const HEADERS = [
  "S.No",
  "User",
  "Device",
  "Native State",
  "Current State",
  "Downloaded",
  "Download",
];

const cellSx = {
  py: "6px",
  px: "10px",
  fontSize: "0.78rem",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #e5e7eb",
};

const headSx = {
  ...cellSx,
  fontWeight: 700,
  bgcolor: "#212529",
  color: "#ffffff",
  py: "9px",
  fontSize: "0.82rem",
};

const filterCardSx = {
  p: 1.5,
  border: "1px solid #e5e7eb",
  borderRadius: 2,
  bgcolor: "#f8fafc",
  minWidth: 0,
};

const filterGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(160px, 1fr))", xl: "repeat(3, minmax(160px, 1fr))" },
  gap: 1.25,
};

export default function ContactList() {
  const [rows, setRows] = React.useState<ContactDeviceRow[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalRows, setTotalRows] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [downloadingDeviceKey, setDownloadingDeviceKey] = React.useState<string | null>(null);
  const [userIdInput, setUserIdInput] = React.useState("");
  const [appliedUserId, setAppliedUserId] = React.useState<number | null>(null);
  const [countries, setCountries] = React.useState<CountryOption[]>([]);
  const [nativeStates, setNativeStates] = React.useState<StateOption[]>([]);
  const [currentStates, setCurrentStates] = React.useState<StateOption[]>([]);
  const [nativeCities, setNativeCities] = React.useState<CityOption[]>([]);
  const [currentCities, setCurrentCities] = React.useState<CityOption[]>([]);
  const [draftFilters, setDraftFilters] = React.useState<ContactListFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = React.useState<ContactListFilters>(EMPTY_FILTERS);
  const [downloadedSort, setDownloadedSort] = React.useState<DownloadedSort>(null);
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastSeverity, setToastSeverity] = React.useState<"success" | "error">("success");
  const [isFilterModalOpen, setIsFilterModalOpen] = React.useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = React.useState(false);
  const [viewContacts, setViewContacts] = React.useState<ContactRow[]>([]);
  const [viewContext, setViewContext] = React.useState<{ userId: number; deviceId: number } | null>(null);
  const [viewLoading, setViewLoading] = React.useState(false);
  const isFetchingRef = React.useRef(false);

  const appendParam = (params: URLSearchParams, key: string, value: number | null | undefined) => {
    if (value !== null && value !== undefined && !Number.isNaN(value)) {
      params.set(key, String(value));
    }
  };

  const buildContactListUrl = React.useCallback((
    page: number,
    size: number,
    userId?: number | null,
    filters: ContactListFilters = EMPTY_FILTERS,
    sort: DownloadedSort = null,
  ) => {
    const params = new URLSearchParams();
    appendParam(params, "userId", userId ?? null);
    appendParam(params, "nativeCountry", filters.nativeCountry);
    appendParam(params, "nativeState", filters.nativeState);
    appendParam(params, "nativeCity", filters.nativeCity);
    appendParam(params, "currentCountry", filters.currentCountry);
    appendParam(params, "currentState", filters.currentState);
    appendParam(params, "currentCity", filters.currentCity);
    if (sort) {
      params.set("downloadedSort", sort);
    }
    const queryString = params.toString();
    return `/v1/contacts/getAllContactDevices/${page}/${size}${queryString ? `?${queryString}` : ""}`;
  }, []);

  const fetchStates = React.useCallback((countryId: number, type: "native" | "current") => {
    axiosClient
      .get(`/v1/states/getByCuntryId/${countryId}`, { headers: { Authorization: getToken() } })
      .then((res) => {
        const data = (res.data ?? []) as StateOption[];
        if (type === "native") {
          setNativeStates(data);
        } else {
          setCurrentStates(data);
        }
      })
      .catch((error) => {
        console.log(error);
        if (type === "native") {
          setNativeStates([]);
        } else {
          setCurrentStates([]);
        }
      });
  }, []);

  const fetchCities = React.useCallback((stateId: number, type: "native" | "current") => {
    axiosClient
      .get(`/v1/cities/getByStateId/${stateId}`, { headers: { Authorization: getToken() } })
      .then((res) => {
        const data = (res.data ?? []) as CityOption[];
        if (type === "native") {
          setNativeCities(data);
        } else {
          setCurrentCities(data);
        }
      })
      .catch((error) => {
        console.log(error);
        if (type === "native") {
          setNativeCities([]);
        } else {
          setCurrentCities([]);
        }
      });
  }, []);

  const fetchPage = React.useCallback((
    page: number,
    size: number,
    userId?: number | null,
    filters: ContactListFilters = EMPTY_FILTERS,
    sort: DownloadedSort = downloadedSort,
  ) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    const url = buildContactListUrl(page, size, userId, filters, sort);

    axiosClient
      .post(url, {}, {
        headers: { Authorization: getToken() },
        skipGlobalLoader: true,
      })
      .then((res) => {
        const content = (res.data?.content ?? []) as ContactDeviceRow[];
        setRows(content);
        setTotalRows(res.data?.totalElements ?? 0);
        setTotalPages(res.data?.totalPages ?? 1);
        setCurrentPage(page);
      })
      .catch((error) => {
        console.log(error);
        setRows([]);
        setToastSeverity("error");
        const backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.response?.data ||
          error?.message;
        setToastMessage(`Failed to load contact list${backendMessage ? `: ${backendMessage}` : "."}`);
        setToastOpen(true);
      })
      .finally(() => {
        isFetchingRef.current = false;
        setIsLoading(false);
      });
  }, [buildContactListUrl, downloadedSort]);

  const exportUserDeviceContacts = (userId: number, deviceId: number) => {
    const downloadKey = `${userId}-${deviceId}`;
    setDownloadingDeviceKey(downloadKey);
    axiosClient
      .get(`/v1/contacts/${userId}?deviceId=${deviceId}`, {
        headers: { Authorization: getToken() },
      })
      .then((res) => {
        const contacts = (res.data ?? []) as ContactRow[];
        if (contacts.length === 0) {
          setToastSeverity("error");
          setToastMessage("No contacts found for the selected device.");
          setToastOpen(true);
          return;
        }
        const headers = ["SN", "Contact ID", "User ID", "Device ID", "Name", "Mobile Number", "Created On"];
        const rowsCsv = [
          headers.join(","),
          ...contacts.map((contact, index) => [
            index + 1,
            contact.id ?? "",
            contact.userId ?? "",
            contact.deviceId ?? "",
            contact.name ?? "",
            contact.mobileNumber ?? "",
            contact.created_at ? moment(contact.created_at).format("DD/MM/YYYY, h:mm a") : "",
          ]
            .map((v) => `"${String(v).replace(/"/g, '""')}"`)
            .join(",")),
        ];

        const blob = new Blob([rowsCsv.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `contacts-user-${userId}-device-${deviceId}-${moment().format("YYYYMMDD-HHmmss")}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        axiosClient.post(
          `/v1/contacts/markDeviceDownloaded?userId=${userId}&deviceId=${deviceId}`,
          {},
          { headers: { Authorization: getToken() } },
        ).catch((markError) => {
          console.log(markError);
        });

        setRows((currentRows) =>
          currentRows.map((currentRow) =>
            currentRow.userId === userId && currentRow.deviceId === deviceId
              ? { ...currentRow, isDownloaded: true }
              : currentRow,
          ),
        );

        setToastSeverity("success");
        setToastMessage(`CSV downloaded for user ${userId}, device ${deviceId}.`);
        setToastOpen(true);
      })
      .catch((error) => {
        console.log(error);
        setToastSeverity("error");
        setToastMessage("Failed to download contacts CSV.");
        setToastOpen(true);
      })
      .finally(() => {
        setDownloadingDeviceKey(null);
      });
  };

  const viewUserDeviceContacts = (userId: number, deviceId: number) => {
    setViewLoading(true);
    setViewContext({ userId, deviceId });
    axiosClient
      .get(`/v1/contacts/${userId}?deviceId=${deviceId}`, {
        headers: { Authorization: getToken() },
      })
      .then((res) => {
        const contacts = (res.data ?? []) as ContactRow[];
        setViewContacts(contacts);
        setIsViewModalOpen(true);
      })
      .catch((error) => {
        console.log(error);
        setToastSeverity("error");
        setToastMessage("Failed to load contacts for viewing.");
        setToastOpen(true);
      })
      .finally(() => {
        setViewLoading(false);
      });
  };

  React.useEffect(() => {
    axiosClient
      .get("/v1/countries", { headers: { Authorization: getToken() } })
      .then((res) => {
        setCountries((res.data ?? []) as CountryOption[]);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  React.useEffect(() => {
    fetchPage(1, pageSize, appliedUserId, appliedFilters, downloadedSort);
  }, [fetchPage, pageSize, appliedUserId, appliedFilters, downloadedSort]);

  const applyFilters = () => {
    const parsed = userIdInput ? Number(userIdInput) : null;
    const nextUserId = parsed && !Number.isNaN(parsed) ? parsed : null;
    setAppliedUserId(nextUserId);
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
    fetchPage(1, pageSize, nextUserId, draftFilters);
    setIsFilterModalOpen(false);
  };

  const clearFilters = () => {
    setUserIdInput("");
    setAppliedUserId(null);
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setNativeStates([]);
    setCurrentStates([]);
    setNativeCities([]);
    setCurrentCities([]);
    setCurrentPage(1);
    fetchPage(1, pageSize, null, EMPTY_FILTERS);
    setIsFilterModalOpen(false);
  };

  const getDownloadedSortLabel = () => {
    if (downloadedSort === "asc") return "Downloaded ↑";
    if (downloadedSort === "desc") return "Downloaded ↓";
    return "Downloaded";
  };

  const toggleDownloadedSort = () => {
    const nextSort: DownloadedSort = downloadedSort === null ? "asc" : downloadedSort === "asc" ? "desc" : null;
    setDownloadedSort(nextSort);
    setCurrentPage(1);
    fetchPage(1, pageSize, appliedUserId, appliedFilters, nextSort);
  };

  return (
    <ScreenLayout padding="medium">
      <div className="app-content">
        <PageHeader
          title="Contact List"
          description={
            <>
              Showing {rows.length} of {totalRows} contacts
              {isLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
            </>
          }
          actions={
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsFilterModalOpen(true)}
                sx={{ minWidth: 120 }}
              >
                Show Filters
              </Button>
            </Box>
          }
        />

        <Dialog
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            Contact Filters
            <IconButton
              aria-label="close"
              onClick={() => setIsFilterModalOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8, color: "#6b7280" }}
            >
              X
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ ...filterCardSx, bgcolor: "#ffffff" }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Quick Filters
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(180px, 220px)" }, gap: 1.25 }}>
                  <TextField
                    size="small"
                    value={userIdInput}
                    onChange={(e) => setUserIdInput(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="Filter by User ID"
                    fullWidth
                  />
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 1.5 }}>
                <Box sx={filterCardSx}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#0f766e", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Native Location
                  </Typography>
                  <Box sx={filterGridSx}>
                    <TextField
                      select
                      size="small"
                      label="Country"
                      value={draftFilters.nativeCountry ?? ""}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        setDraftFilters((prev) => ({ ...prev, nativeCountry: value, nativeState: null, nativeCity: null }));
                        setNativeStates([]);
                        setNativeCities([]);
                        if (value) {
                          fetchStates(value, "native");
                        }
                      }}
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="State"
                      value={draftFilters.nativeState ?? ""}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        setDraftFilters((prev) => ({ ...prev, nativeState: value, nativeCity: null }));
                        setNativeCities([]);
                        if (value) {
                          fetchCities(value, "native");
                        }
                      }}
                      disabled={!draftFilters.nativeCountry}
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {nativeStates.map((state) => (
                        <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="City"
                      value={draftFilters.nativeCity ?? ""}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        setDraftFilters((prev) => ({ ...prev, nativeCity: value }));
                      }}
                      disabled={!draftFilters.nativeState}
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {nativeCities.map((city) => (
                        <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>

                <Box sx={filterCardSx}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1d4ed8", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Current Location
                  </Typography>
                  <Box sx={filterGridSx}>
                    <TextField
                      select
                      size="small"
                      label="Country"
                      value={draftFilters.currentCountry ?? ""}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        setDraftFilters((prev) => ({ ...prev, currentCountry: value, currentState: null, currentCity: null }));
                        setCurrentStates([]);
                        setCurrentCities([]);
                        if (value) {
                          fetchStates(value, "current");
                        }
                      }}
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="State"
                      value={draftFilters.currentState ?? ""}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        setDraftFilters((prev) => ({ ...prev, currentState: value, currentCity: null }));
                        setCurrentCities([]);
                        if (value) {
                          fetchCities(value, "current");
                        }
                      }}
                      disabled={!draftFilters.currentCountry}
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {currentStates.map((state) => (
                        <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="City"
                      value={draftFilters.currentCity ?? ""}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null;
                        setDraftFilters((prev) => ({ ...prev, currentCity: value }));
                      }}
                      disabled={!draftFilters.currentState}
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {currentCities.map((city) => (
                        <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={clearFilters} variant="outlined">Clear</Button>
            <Button onClick={applyFilters} variant="contained" sx={{ background: "#212529", color: "#ffffff", "&:hover": { background: "#343a40" } }}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            Device Contacts {viewContext ? `(User ${viewContext.userId}, Device ${viewContext.deviceId})` : ""}
            <IconButton
              aria-label="close"
              onClick={() => setIsViewModalOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8, color: "#6b7280" }}
            >
              X
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {viewContacts.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                No contacts found for this device.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headSx}>S.No</TableCell>
                    <TableCell sx={headSx}>Name</TableCell>
                    <TableCell sx={headSx}>Mobile Number</TableCell>
                    <TableCell sx={headSx}>Created On</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewContacts.map((contact, index) => (
                    <TableRow key={contact.id ?? `${contact.mobileNumber ?? "contact"}-${index}`}>
                      <TableCell sx={cellSx}>{index + 1}</TableCell>
                      <TableCell sx={cellSx}>{contact.name ?? "-"}</TableCell>
                      <TableCell sx={cellSx}>{contact.mobileNumber ?? "-"}</TableCell>
                      <TableCell sx={cellSx}>
                        {contact.created_at ? moment(contact.created_at).format("DD/MM/YYYY, h:mm a") : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setIsViewModalOpen(false)} variant="outlined">Close</Button>
          </DialogActions>
        </Dialog>

        <Paper
          sx={{
            borderRadius: 2,
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
            position: "relative",
            opacity: isLoading ? 0.65 : 1,
            transition: "opacity 0.2s",
          }}
        >
          <TableContainer
            sx={{
              height: { xs: "calc(100vh - 340px)", sm: "calc(100vh - 260px)" },
              overflow: "auto",
              borderRadius: 2,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {HEADERS.map((header) => {
                    if (header === "Downloaded") {
                      return (
                        <TableCell key={header} sx={headSx}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={toggleDownloadedSort}
                            sx={{ color: "#ffffff", minWidth: 0, p: 0, fontSize: "0.82rem", textTransform: "none", fontWeight: 700 }}
                          >
                            {getDownloadedSortLabel()}
                          </Button>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={header} sx={headSx}>
                        {header}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.userId}-${row.deviceId}`} hover sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                    <TableCell sx={cellSx}>{(currentPage - 1) * pageSize + index + 1}</TableCell>
                    <TableCell sx={cellSx}>{row.userId}</TableCell>
                    <TableCell sx={cellSx}>{row.label}</TableCell>
                    <TableCell sx={cellSx}>{row.nativeState || "-"}</TableCell>
                    <TableCell sx={cellSx}>{row.currentState || "-"}</TableCell>
                    <TableCell sx={cellSx}>{String(Boolean(row.isDownloaded))}</TableCell>
                    <TableCell sx={cellSx}>
                      <button
                        className="btn btn-sm me-2"
                        disabled={viewLoading && viewContext?.userId === row.userId && viewContext?.deviceId === row.deviceId}
                        onClick={() => viewUserDeviceContacts(row.userId, row.deviceId)}
                      >
                        {viewLoading && viewContext?.userId === row.userId && viewContext?.deviceId === row.deviceId ? "Loading..." : "View"}
                      </button>
                      <button
                        className="btn btn-sm"
                        disabled={downloadingDeviceKey === `${row.userId}-${row.deviceId}`}
                        onClick={() => exportUserDeviceContacts(row.userId, row.deviceId)}
                      >
                        {downloadingDeviceKey === `${row.userId}-${row.deviceId}` ? "Downloading..." : "Download"}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}

                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        No contacts found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 2 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Rows per page:
            </Typography>
            <CustomSelect
              value={pageSize}
              onChange={(value) => {
                if (isLoading) return;
                const nextSize = value as number;
                setPageSize(nextSize);
                fetchPage(1, nextSize, appliedUserId);
              }}
              options={[10, 25, 50, 100]}
              minWidth={80}
            />
          </Box>

          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => {
              if (isLoading) return;
              fetchPage(page, pageSize, appliedUserId);
            }}
            color="primary"
            size="small"
            siblingCount={0}
            disabled={isLoading}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#212529",
                "&.Mui-selected": {
                  backgroundColor: "#212529",
                  color: "#ffffff",
                },
              },
            }}
          />
        </Box>

        <Snackbar
          open={toastOpen}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={toastSeverity}
            variant="filled"
            onClose={() => setToastOpen(false)}
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </div>
    </ScreenLayout>
  );
}
